import {
	ALL_SECTIONS,
	MOBILE_PRIMARY_NAV,
	PRIMARY_TABS,
	ROUTES,
} from "@/lib/routes";
import { NavIconBadge } from "@/components/ui/NavIconBadge";
import { cn } from "@/lib/utils";
import {
	BarChart3,
	Coins,
	Gavel,
	Landmark,
	MoreHorizontal,
	ArrowLeftRight,
	Scale,
	Vault,
	Wallet,
	X,
	type LucideIcon,
} from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

const ICON_BY_PATH: Record<string, LucideIcon> = {
	[ROUTES.borrow]: Coins,
	[ROUTES.vaults]: Vault,
	[ROUTES.liquidation]: Gavel,
	[ROUTES.analytics]: BarChart3,
	[ROUTES.treasury]: Landmark,
	[ROUTES.governance]: Scale,
	[ROUTES.wallet]: Wallet,
	[ROUTES.pegswap]: ArrowLeftRight,
	[ROUTES.liquidity]: ArrowLeftRight,
	[ROUTES.more]: MoreHorizontal,
};

function navClass(isActive: boolean) {
	return cn(
		"inline-flex min-h-touch shrink-0 items-center gap-1.5 rounded-xl px-3 text-xs font-semibold transition sm:text-sm",
		isActive
			? "bg-primary/12 text-primary"
			: "text-muted-foreground hover:bg-muted hover:text-foreground",
	);
}

/**
 * Desktop (md+): full product nav row.
 * Mobile: primary chips (Borrow · Vaults · Liquidate · Wallet) + More → sheet.
 */
export function PrimaryNavRow() {
	const { pathname } = useLocation();
	const [moreOpen, setMoreOpen] = useState(false);
	const sheetTitleId = useId();
	const closeBtnRef = useRef<HTMLButtonElement>(null);

	const moreActive =
		ALL_SECTIONS.some(
			(l) =>
				!MOBILE_PRIMARY_NAV.some((p) => p.to === l.to) &&
				(pathname === l.to || pathname.startsWith(`${l.to}/`)),
		) || pathname === ROUTES.more;

	useEffect(() => {
		setMoreOpen(false);
	}, [pathname]);

	useEffect(() => {
		if (!moreOpen) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setMoreOpen(false);
		};
		document.addEventListener("keydown", onKey);
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		closeBtnRef.current?.focus();
		return () => {
			document.removeEventListener("keydown", onKey);
			document.body.style.overflow = prev;
		};
	}, [moreOpen]);

	return (
		<>
			<nav
				className="border-b border-border bg-card/95 backdrop-blur-xl"
				aria-label="Product"
			>
				{/* Mobile: primary + More */}
				<div className="container mx-auto flex items-stretch gap-0.5 px-2 py-1.5 sm:px-4 md:hidden">
					{MOBILE_PRIMARY_NAV.map(({ to, label }) => {
						const Icon = ICON_BY_PATH[to] ?? Coins;
						return (
							<NavLink
								key={to}
								to={to}
								className={({ isActive }) =>
									cn(navClass(isActive), "flex-1 justify-center px-2")
								}
							>
								<Icon className="size-4 shrink-0" aria-hidden />
								<span className="truncate">{label}</span>
							</NavLink>
						);
					})}
					<button
						type="button"
						className={cn(navClass(moreActive || moreOpen), "flex-1 justify-center px-2")}
						aria-expanded={moreOpen}
						aria-haspopup="dialog"
						onClick={() => setMoreOpen(true)}
					>
						<MoreHorizontal className="size-4 shrink-0" aria-hidden />
						More
					</button>
				</div>

				{/* Desktop: full product set */}
				<div className="container mx-auto hidden flex-wrap items-stretch gap-1 px-4 py-1.5 md:flex">
					{PRIMARY_TABS.map(({ to, label }) => {
						const Icon = ICON_BY_PATH[to] ?? Coins;
						return (
							<NavLink key={to} to={to} className={({ isActive }) => navClass(isActive)}>
								<Icon className="size-4 shrink-0" aria-hidden />
								{label}
							</NavLink>
						);
					})}
					<NavLink
						to={ROUTES.more}
						className={({ isActive }) => navClass(isActive)}
					>
						<MoreHorizontal className="size-4 shrink-0" aria-hidden />
						More
					</NavLink>
				</div>
			</nav>

			{/* More bottom sheet */}
			{moreOpen ? (
				<div className="fixed inset-0 z-50 md:hidden" role="presentation">
					<button
						type="button"
						className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
						aria-label="Close menu"
						onClick={() => setMoreOpen(false)}
					/>
					<div
						role="dialog"
						aria-modal="true"
						aria-labelledby={sheetTitleId}
						className="absolute inset-x-0 bottom-0 max-h-[min(82vh,680px)] overflow-y-auto rounded-t-2xl border border-border bg-card pb-[max(1rem,env(safe-area-inset-bottom))] shadow-lift"
					>
						<div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-4 py-3 backdrop-blur-xl">
							<p id={sheetTitleId} className="text-sm font-bold">
								All sections
							</p>
							<button
								ref={closeBtnRef}
								type="button"
								className="inline-flex min-h-touch min-w-touch items-center justify-center rounded-xl border border-border text-muted-foreground hover:text-foreground"
								aria-label="Close"
								onClick={() => setMoreOpen(false)}
							>
								<X className="size-5" />
							</button>
						</div>
						<ul className="grid gap-2 p-3">
							{ALL_SECTIONS.map((s) => {
								const active =
									pathname === s.to || pathname.startsWith(`${s.to}/`);
								return (
									<li key={s.to + s.label}>
										<NavLink
											to={s.to}
											className={cn(
												"flex min-h-[4.25rem] items-start gap-3 rounded-xl border border-transparent px-3 py-2.5",
												active
													? "border-primary/20 bg-primary/12 text-primary"
													: "text-foreground hover:bg-muted",
											)}
											onClick={() => setMoreOpen(false)}
										>
											<span className="mt-0.5 shrink-0">
												<NavIconBadge icon={s.icon} size="sm" active={active} />
											</span>
											<span className="min-w-0 flex-1">
												<span className="block truncate text-sm font-semibold">
													{s.label}
												</span>
												<span className="mt-0.5 line-clamp-2 min-h-[2.25rem] text-[11px] leading-snug text-muted-foreground">
													{s.desc}
												</span>
											</span>
										</NavLink>
									</li>
								);
							})}
						</ul>
					</div>
				</div>
			) : null}
		</>
	);
}
